import { useCallback, useEffect, useRef, useState } from 'react';

import { useMenuSelection } from 'global/MenuSelectionContext';
import { useIsMobile } from 'utils/mediaQueries';

// Importação do hook (sempre importa, mas pode falhar se o contexto não estiver disponível)

interface SmartReloadOptions {
  minInterval?: number; // Intervalo mínimo entre recarregamentos (ms)
  meetingProtection?: boolean; // Se deve proteger reuniões
  checkInterval?: number; // Intervalo para verificar reuniões (ms)
  enableMenuDetection?: boolean; // Se deve usar detecção por menu (padrão: true)
}

// Wrapper para capturar erros do hook
const useMenuSelectionSafe = () => {
  try {
    return useMenuSelection();
  } catch {
    console.log('[SmartReload] MenuSelectionContext não disponível, usando detecção básica');
    return {
      thirdLevelMenu: null,
      secondLevelMenu: null,
      firstLevelMenu: null,
      currentLevel: null,
    };
  }
};

export const useSmartReload = (options: SmartReloadOptions = {}) => {
  const {
    minInterval = 120000, // 2 minutos padrão
    meetingProtection = true,
    checkInterval = 5000, // 5 segundos
    enableMenuDetection = true,
  } = options;

  const [isInMeeting, setIsInMeeting] = useState(false);
  const [lastReloadTime, setLastReloadTime] = useState(0);
  const [reloadCount, setReloadCount] = useState(0);
  const [isReloadBlocked, setIsReloadBlocked] = useState(false);
  const lastReloadReason = useRef<string>('');

  // Hook para detectar mobile
  const isMobile = useIsMobile();

  // Sempre chama o hook (agora em um wrapper separado)
  const menuContext = useMenuSelectionSafe();

  // Extrai dados do menu de forma segura
  const thirdLevelMenu = enableMenuDetection ? menuContext?.thirdLevelMenu || null : null;
  const secondLevelMenu = enableMenuDetection ? menuContext?.secondLevelMenu || null : null;
  const firstLevelMenu = enableMenuDetection ? menuContext?.firstLevelMenu || null : null;
  const currentLevel = enableMenuDetection ? menuContext?.currentLevel || null : null;

  // Detecta se está em reunião Jitsi
  useEffect(() => {
    if (!meetingProtection) return;

    const checkIfInMeeting = () => {
      try {
        // 1. Verifica se existe iframe do Jitsi (detecção visual)
        const jitsiFrame = document.querySelector('iframe[id^="jitsiConferenceFrame"]');
        const isInJitsi = !!jitsiFrame;

        // 2. Verifica se há elementos específicos do Jitsi
        const hasJitsiElements =
          document.querySelector('.jitsi-iframe') ||
          document.querySelector('[data-jitsi]') ||
          document.querySelector('.JaaSMeeting') ||
          document.querySelector('[class*="jitsi"]') ||
          document.querySelector('[class*="JaaSMeeting"]');

        // 3. Verifica se o menu atual é de reunião (detecção por dynamic_component)
        const currentMenu = thirdLevelMenu || secondLevelMenu || firstLevelMenu;
        const isMeetingMenu =
          (enableMenuDetection && currentMenu?.feature?.dynamic_component === 'MEETING_ROOM') ||
          (enableMenuDetection && currentMenu?.feature?.dynamic_component === 'MEETING_ROOM_ROOM');

        // 4. Verifica se está em uma página de reunião (URL)
        const isInMeetingPage =
          window.location.pathname.includes('/rooms/') ||
          window.location.pathname.includes('/meeting/');

        // 5. Verifica se há componentes Jitsi ativos
        const hasActiveJitsiComponent =
          document.querySelector('[class*="JaaSMeeting"]') ||
          document.querySelector('[class*="jitsi"]') ||
          document.querySelector('iframe[src*="jitsi"]') ||
          document.querySelector('div[class*="jitsi"]');

        // 6. Verifica se há componentes Jitsi em tela cheia (mobile)
        const hasFullscreenJitsi =
          isMobile &&
          (document.querySelector('[style*="position: fixed"]') ||
            document.querySelector('[style*="100vw"]') ||
            document.querySelector('[style*="100vh"]'));

        // Combina todas as verificações
        const meetingDetected =
          isInJitsi ||
          isMeetingMenu ||
          isInMeetingPage ||
          !!hasJitsiElements ||
          !!hasActiveJitsiComponent ||
          !!hasFullscreenJitsi;

        if (meetingDetected !== isInMeeting) {
          console.log('[SmartReload] Status de reunião mudou:', {
            from: isInMeeting,
            to: meetingDetected,
            isMobile,
            enableMenuDetection,
            reason: {
              jitsiFrame: !!jitsiFrame,
              isMeetingMenu,
              meetingPage: isInMeetingPage,
              jitsiElements: !!hasJitsiElements,
              activeJitsiComponent: !!hasActiveJitsiComponent,
              fullscreenJitsi: !!hasFullscreenJitsi,
              currentMenu: currentMenu?.label,
              dynamicComponent: currentMenu?.feature?.dynamic_component,
              currentLevel,
            },
          });
          setIsInMeeting(meetingDetected);
        }
      } catch (error) {
        console.error('[SmartReload] Erro ao verificar reunião:', error);
      }
    };

    // Verifica imediatamente
    checkIfInMeeting();

    // Verifica periodicamente
    const interval = setInterval(checkIfInMeeting, checkInterval);
    return () => clearInterval(interval);
  }, [
    meetingProtection,
    checkInterval,
    isInMeeting,
    thirdLevelMenu,
    secondLevelMenu,
    firstLevelMenu,
    currentLevel,
    isMobile,
    enableMenuDetection,
  ]);

  // Função principal para lidar com problemas de tema
  const handleThemeIssue = useCallback(
    (reason: string = 'tema') => {
      const now = Date.now();
      const timeSinceLastReload = now - lastReloadTime;

      // Log detalhado para debugging
      console.log('[SmartReload] Verificando recarregamento:', {
        reason,
        isInMeeting,
        isMobile,
        enableMenuDetection,
        timeSinceLastReload,
        minInterval,
        lastReloadTime,
        reloadCount,
        isReloadBlocked,
        currentMenu: thirdLevelMenu || secondLevelMenu || firstLevelMenu,
        dynamicComponent: (thirdLevelMenu || secondLevelMenu || firstLevelMenu)?.feature
          ?.dynamic_component,
      });

      // Não recarrega se:
      // 1. Está em reunião (proteção crítica)
      if (isInMeeting && meetingProtection) {
        console.warn('[SmartReload] Recarregamento BLOQUEADO: Usuário em reunião Jitsi', {
          reason,
          isInMeeting,
          isMobile,
          enableMenuDetection,
          currentMenu: (thirdLevelMenu || secondLevelMenu || firstLevelMenu)?.label,
          dynamicComponent: (thirdLevelMenu || secondLevelMenu || firstLevelMenu)?.feature
            ?.dynamic_component,
        });
        setIsReloadBlocked(true);
        lastReloadReason.current = reason;

        // Dispara evento para notificar outros componentes
        window.dispatchEvent(
          new CustomEvent('reloadBlocked', {
            detail: { reason, isInMeeting, isMobile },
          })
        );
        return;
      }

      // 2. Recarregou há muito pouco tempo
      if (timeSinceLastReload < minInterval) {
        console.warn('[SmartReload] Recarregamento BLOQUEADO: Intervalo muito curto', {
          timeSinceLastReload,
          minInterval,
          isMobile,
        });
        setIsReloadBlocked(true);
        lastReloadReason.current = reason;
        return;
      }

      // 3. É a primeira vez (pode ser carregamento normal)
      if (lastReloadTime === 0) {
        console.log('[SmartReload] Primeira verificação, não recarregando', {
          isMobile,
          enableMenuDetection,
        });
        setLastReloadTime(now);
        return;
      }

      // 4. Muitos recarregamentos em sequência (proteção contra loop)
      if (reloadCount > 3) {
        console.error('[SmartReload] Muitos recarregamentos, bloqueando para evitar loop', {
          isMobile,
          enableMenuDetection,
        });
        setIsReloadBlocked(true);
        lastReloadReason.current = 'muitos_recarregamentos';
        return;
      }

      // Se chegou aqui, pode recarregar
      console.log('[SmartReload] Recarregando aplicação:', {
        reason,
        reloadCount: reloadCount + 1,
        timeSinceLastReload,
        isMobile,
        enableMenuDetection,
        currentMenu: (thirdLevelMenu || secondLevelMenu || firstLevelMenu)?.label,
      });

      setLastReloadTime(now);
      setReloadCount(prev => prev + 1);
      setIsReloadBlocked(false);
      lastReloadReason.current = '';

      // Pequeno delay para permitir logs
      setTimeout(() => {
        window.location.reload();
      }, 100);
    },
    [
      isInMeeting,
      meetingProtection,
      lastReloadTime,
      minInterval,
      reloadCount,
      thirdLevelMenu,
      secondLevelMenu,
      firstLevelMenu,
      isMobile,
      enableMenuDetection,
      isReloadBlocked,
    ]
  );

  // Função para forçar recarregamento (usado pelo usuário)
  const forceReload = useCallback(() => {
    console.log('[SmartReload] Recarregamento forçado pelo usuário', {
      isMobile,
      enableMenuDetection,
    });
    setLastReloadTime(Date.now());
    setReloadCount(0);
    setIsReloadBlocked(false);
    lastReloadReason.current = '';
    window.location.reload();
  }, [isMobile, enableMenuDetection]);

  // Função para resetar contadores
  const resetCounters = useCallback(() => {
    console.log('[SmartReload] Resetando contadores', { isMobile, enableMenuDetection });
    setReloadCount(0);
    setIsReloadBlocked(false);
    lastReloadReason.current = '';
  }, [isMobile, enableMenuDetection]);

  // Função para verificar se pode recarregar
  const canReload = useCallback(() => {
    const now = Date.now();
    const timeSinceLastReload = now - lastReloadTime;

    return (
      !isInMeeting && timeSinceLastReload >= minInterval && reloadCount <= 3 && !isReloadBlocked
    );
  }, [isInMeeting, lastReloadTime, minInterval, reloadCount, isReloadBlocked]);

  // Salva estatísticas no localStorage para debugging
  useEffect(() => {
    const stats = {
      lastReloadTime,
      reloadCount,
      isReloadBlocked,
      lastReloadReason: lastReloadReason.current,
      isInMeeting,
      isMobile,
      enableMenuDetection,
      currentMenu: (thirdLevelMenu || secondLevelMenu || firstLevelMenu)?.label,
      dynamicComponent: (thirdLevelMenu || secondLevelMenu || firstLevelMenu)?.feature
        ?.dynamic_component,
      currentLevel,
      timestamp: Date.now(),
    };

    try {
      localStorage.setItem('smart_reload_stats', JSON.stringify(stats));
    } catch (error) {
      console.error('[SmartReload] Erro ao salvar estatísticas:', error);
    }
  }, [
    lastReloadTime,
    reloadCount,
    isReloadBlocked,
    isInMeeting,
    thirdLevelMenu,
    secondLevelMenu,
    firstLevelMenu,
    currentLevel,
    isMobile,
    enableMenuDetection,
  ]);

  // Carrega estatísticas do localStorage na inicialização
  useEffect(() => {
    try {
      const savedStats = localStorage.getItem('smart_reload_stats');
      if (savedStats) {
        const stats = JSON.parse(savedStats);
        const now = Date.now();

        // Só carrega se não for muito antigo (1 hora)
        if (now - stats.timestamp < 60 * 60 * 1000) {
          setLastReloadTime(stats.lastReloadTime || 0);
          setReloadCount(stats.reloadCount || 0);
          setIsReloadBlocked(stats.isReloadBlocked || false);
          lastReloadReason.current = stats.lastReloadReason || '';

          console.log('[SmartReload] Estatísticas carregadas:', stats);
        }
      }
    } catch (error) {
      console.error('[SmartReload] Erro ao carregar estatísticas:', error);
    }
  }, []);

  return {
    handleThemeIssue,
    forceReload,
    resetCounters,
    canReload,
    isInMeeting,
    isReloadBlocked,
    reloadCount,
    lastReloadReason: lastReloadReason.current,
    stats: {
      lastReloadTime,
      reloadCount,
      isReloadBlocked,
      isInMeeting,
      isMobile,
      enableMenuDetection,
      currentMenu: (thirdLevelMenu || secondLevelMenu || firstLevelMenu)?.label,
      dynamicComponent: (thirdLevelMenu || secondLevelMenu || firstLevelMenu)?.feature
        ?.dynamic_component,
      currentLevel,
    },
  };
};
